/**
 * NotificationToast — Hiển thị toast popup khi nhận thông báo mới trong app.
 *
 * Khác với react-hot-toast (dùng cho messages hệ thống), component này:
 * - Hiển thị ở góc dưới phải (như Facebook/Zalo)
 * - Có icon theo loại thông báo (attendance, leave, ...)
 * - Tự động dismiss sau 5 giây
 * - Có thể click để navigate đến action_url
 * - Queue tối đa 3 toasts cùng lúc
 * - Hỗ trợ dark mode
 */

import React, { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { create } from 'zustand';
import {
  Bell,
  Info,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Calendar,
  CalendarDays,
  Clock,
  Shield,
  X,
} from 'lucide-react';

// ── Toast Store ──────────────────────────────────────────────────────────────

const MAX_TOASTS = 3;
const AUTO_DISMISS_MS = 5000;

export const useToastStore = create((set, get) => ({
  toasts: [],

  addToast: (notification) => {
    const id = notification.notification_id || `toast-${Date.now()}`;
    // Don't duplicate
    if (get().toasts.find((t) => t.id === id)) return;

    const toast = { ...notification, id, createdAt: Date.now() };
    set((state) => ({
      toasts: [toast, ...state.toasts].slice(0, MAX_TOASTS),
    }));
  },

  removeToast: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }));
  },

  clearAll: () => set({ toasts: [] }),
}));

// ── Config maps ──────────────────────────────────────────────────────────────

const typeConfig = {
  info:    { icon: Info,          color: 'text-blue-500',  bg: 'bg-blue-50 dark:bg-blue-900/30',  border: 'border-blue-200 dark:border-blue-700' },
  success: { icon: CheckCircle,   color: 'text-green-500', bg: 'bg-green-50 dark:bg-green-900/30', border: 'border-green-200 dark:border-green-700' },
  warning: { icon: AlertTriangle, color: 'text-amber-500', bg: 'bg-amber-50 dark:bg-amber-900/30', border: 'border-amber-200 dark:border-amber-700' },
  error:   { icon: XCircle,       color: 'text-red-500',   bg: 'bg-red-50 dark:bg-red-900/30',    border: 'border-red-200 dark:border-red-700' },
};

const categoryConfig = {
  attendance: { icon: Calendar,     label: 'Chấm công' },
  leave:      { icon: CalendarDays, label: 'Nghỉ phép' },
  overtime:   { icon: Clock,        label: 'Tăng ca' },
  approval:   { icon: CheckCircle,  label: 'Phê duyệt' },
  system:     { icon: Shield,       label: 'Hệ thống' },
};

// ── Single Toast Item ────────────────────────────────────────────────────────

function ToastItem({ toast, onDismiss }) {
  const navigate = useNavigate();
  const timerRef = useRef(null);
  const [progress, setProgress] = React.useState(100);
  const [exiting, setExiting] = React.useState(false);

  const dismiss = useCallback(() => {
    setExiting(true);
    setTimeout(() => onDismiss(toast.id), 280);
  }, [onDismiss, toast.id]);

  // Auto dismiss with progress bar
  useEffect(() => {
    const start = Date.now();
    const tick = () => {
      const elapsed = Date.now() - start;
      const pct = Math.max(0, 100 - (elapsed / AUTO_DISMISS_MS) * 100);
      setProgress(pct);
      if (pct <= 0) {
        dismiss();
      } else {
        timerRef.current = requestAnimationFrame(tick);
      }
    };
    timerRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(timerRef.current);
  }, [dismiss]);

  const handleClick = () => {
    dismiss();
    if (toast.action_url) {
      navigate(toast.action_url);
    }
  };

  const tc = typeConfig[toast.type] || typeConfig.info;
  const cc = categoryConfig[toast.category] || categoryConfig.system;
  const TypeIcon = tc.icon;
  const CategoryIcon = cc.icon;

  return (
    <div
      className={`
        relative flex items-start gap-3 p-3.5 rounded-xl shadow-lg border
        w-80 max-w-[calc(100vw-2rem)] cursor-pointer overflow-hidden
        transition-all duration-280
        ${tc.bg} ${tc.border}
        ${exiting ? 'opacity-0 translate-x-4 scale-95' : 'opacity-100 translate-x-0 scale-100'}
      `}
      style={{ transition: 'opacity 0.28s ease, transform 0.28s ease' }}
      onClick={handleClick}
    >
      {/* Type icon */}
      <div className={`flex-shrink-0 mt-0.5 ${tc.color}`}>
        <TypeIcon className="w-5 h-5" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 pr-6">
        <div className="flex items-center gap-1.5 mb-0.5">
          <CategoryIcon className="w-3 h-3 text-gray-400 flex-shrink-0" />
          <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">
            {cc.label}
          </span>
        </div>
        <p className="text-sm font-semibold text-gray-900 dark:text-white leading-tight">
          {toast.title}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
          {toast.message}
        </p>
      </div>

      {/* Dismiss button */}
      <button
        onClick={(e) => { e.stopPropagation(); dismiss(); }}
        className="absolute top-2 right-2 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-white/50 dark:hover:bg-gray-700/50 transition-colors"
      >
        <X className="w-3.5 h-3.5" />
      </button>

      {/* Progress bar */}
      <div
        className="absolute bottom-0 left-0 h-0.5 bg-current opacity-30 transition-none"
        style={{ width: `${progress}%`, color: tc.color.replace('text-', '') }}
      />
    </div>
  );
}

// ── Toast Container ──────────────────────────────────────────────────────────

export default function NotificationToast() {
  const { toasts, removeToast } = useToastStore();

  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] right-[calc(1rem+env(safe-area-inset-right))] z-[60] flex flex-col-reverse gap-2 items-end"
      aria-live="polite"
      aria-label="Thông báo mới"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={removeToast} />
      ))}
    </div>
  );
}
